// Shared on-map control primitive — one 48/52px touch target (or a pill with a
// word, see `text`) with idle / active /
// pressed / focus states and a required a11y label, used by both the partner rider
// rail and the client tracking map so the two maps speak one control language.
//
// Pure UI (no mapbox): the caller derives `tone`/`active` from its own
// MapCameraController state and passes them in. This component never owns or
// mutates camera state — it only reflects it.

import { cn } from "@bednbody/ui/lib/utils"
import type { ComponentPropsWithoutRef, ReactNode } from "react"

type MapControlTone = "surface" | "follow" | "active"
type MapControlSize = "primary" | "compact"

const TONE: Record<MapControlTone, string> = {
  // Idle control — white card / dark icon.
  surface: "text-foreground hover:bg-muted",
  // Heading-up follow: the one filled control, carrying the same weight as the CTA.
  follow:
    "bg-primary text-primary-foreground hover:bg-primary/90 ring-1 ring-inset ring-primary-foreground/30",
  // Centered / overview-active / the picked view: soft mint so it reads "on"
  // without shouting teal.
  active: "bg-secondary font-bold text-primary hover:bg-secondary",
}

const SIZE: Record<MapControlSize, string> = {
  primary: "size-[52px]", // primary map controls
  compact: "size-12", //     48px — the minimum touch target
}

/** Same heights, width from the word — see `text`. */
const PILL_SIZE: Record<MapControlSize, string> = {
  primary: "h-[52px] px-4",
  compact: "h-12 px-3.5",
}

export interface MapControlButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "children"> {
  /** Required — every control is icon-only, so it MUST be labelled for AT. */
  label: string
  icon: ReactNode
  tone?: MapControlTone
  size?: MapControlSize
  /** Toggle controls (recenter/overview) pass this → exposes `aria-pressed`. */
  active?: boolean
  /** Standalone (e.g. compass) carries its own card surface; grouped buttons don't. */
  standalone?: boolean
  /** A visible word beside the icon turns the control into a pill — for the
      one control that must read at a glance (the rider's Drive). Keep it
      inside `label`, so the spoken name matches what is on screen. */
  text?: string
}

export function MapControlButton({
  label,
  icon,
  tone = "surface",
  size = "primary",
  active,
  standalone = false,
  text,
  className,
  type = "button",
  ...props
}: MapControlButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={cn(
        "grid shrink-0 place-items-center outline-none transition-all",
        "focus-visible:ring-3 focus-visible:ring-ring/40 active:scale-[0.96]",
        "disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none",
        text ? PILL_SIZE[size] : SIZE[size],
        text && "grid-flow-col gap-2",
        TONE[tone],
        // 2026-10-29: the soft square of the app's buttons (12px), solid white,
        // not round glass bubbles.
        standalone && "rounded-xl shadow-card ring-1 ring-border/70",
        // The card surface must not paint over the one filled tone — it did,
        // and a "follow" control read as white-on-white.
        standalone && tone !== "follow" && "bg-card",
        className,
      )}
      {...props}
    >
      {icon}
      {text ? <span className="text-sm font-bold">{text}</span> : null}
    </button>
  )
}

export interface MapControlGroupProps extends ComponentPropsWithoutRef<"div"> {
  orientation?: "vertical" | "horizontal"
}

/**
 * A rounded card that segments several {@link MapControlButton}s (e.g. zoom +/-,
 * recenter + overview) with hairline dividers. The group owns the surface, so its
 * children render transparent — pass grouped buttons WITHOUT `standalone`.
 */
export function MapControlGroup({
  orientation = "vertical",
  className,
  children,
  ...props
}: MapControlGroupProps) {
  return (
    <div
      className={cn(
        "flex overflow-hidden rounded-xl bg-card shadow-card ring-1 ring-border/70",
        orientation === "vertical"
          ? "flex-col [&>button+button]:border-t"
          : "flex-row [&>button+button]:border-l",
        "[&>button+button]:border-border/70",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}
